/* eslint-disable no-console */
import { LogLevel } from './log-level'
import type { Logger } from './logger'

type ActiveLogLevel = Exclude<LogLevel, LogLevel.OFF>

const LogLevelToConsoleMethod: Record<ActiveLogLevel, Lowercase<keyof typeof LogLevel> & keyof Console> = {
  [LogLevel.DEBUG]: 'debug',
  [LogLevel.INFO]: 'info',
  [LogLevel.WARN]: 'warn',
  [LogLevel.ERROR]: 'error',
}

export type DefaultLoggerOptions = {
  level: LogLevel
  usePrefix: string | string[]
}

const DEFAULT_OPTIONS: DefaultLoggerOptions = {
  level: LogLevel.INFO,
  usePrefix: [],
}

/**
 * Logger implementation which prints log messages to browser console.
 *
 * TODO(roman):
 * - optional decorators: log level, calling method/class
 * - consider masking sensitive data within prints (wallet address etc). A la - https://raw.githubusercontent.com/tahowallet/extension/main/background/lib/logger.ts
 * -    the above will require stringifing all log args, as objects printed to log might have sensitive information.
 * - consider enabling setLevel() at runtime. This will result a need to persist the level for fluent DX cross page navigation.
 * - delegate for farther processing of log prints (for example - sending errors to remote server).
 */
export class DefaultLogger implements Logger {
  private readonly level: LogLevel
  private readonly prefix: string[]

  constructor(options?: Partial<DefaultLoggerOptions>) {
    const ops = { ...DEFAULT_OPTIONS, ...options }

    this.level = ops.level
    this.prefix = typeof ops.usePrefix === 'string' ? [ops.usePrefix] : ops.usePrefix
  }

  isDebugEnabled(): boolean {
    return this.isLevelEnabled(LogLevel.DEBUG)
  }
  debug(message: string, ...args: unknown[]): void {
    this.maybeCallConsole(LogLevel.DEBUG, message, args)
  }
  isInfoEnabled(): boolean {
    return this.isLevelEnabled(LogLevel.INFO)
  }
  info(message: string, ...args: unknown[]): void {
    this.maybeCallConsole(LogLevel.INFO, message, args)
  }
  isWarnEnabled(): boolean {
    return this.isLevelEnabled(LogLevel.WARN)
  }
  warn(message: string, ...args: unknown[]): void {
    this.maybeCallConsole(LogLevel.WARN, message, args)
  }
  isErrorEnabled(): boolean {
    return this.isLevelEnabled(LogLevel.ERROR)
  }
  error(message: string, ...args: unknown[]): void {
    this.maybeCallConsole(LogLevel.ERROR, message, args)
  }

  private isLevelEnabled(testedLevel: LogLevel) {
    return testedLevel >= this.level
  }

  child(tag: string): Logger {
    return new DefaultLogger({ level: this.level, usePrefix: [...this.prefix, tag] })
  }

  private maybeCallConsole(level: ActiveLogLevel, message: string, extraArgs: unknown[]) {
    if (!this.isLevelEnabled(level)) {
      return
    }

    console[LogLevelToConsoleMethod[level]](...this.prefix, message, ...extraArgs)
  }
}
